import { useState } from "react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLock,
  faEye,
  faEyeSlash,
  faSave,
  faCheckCircle,
  faExclamationCircle,
  faShield,
  faKey,
} from "@fortawesome/free-solid-svg-icons";
import { toastError, toastSuccess } from "../../utils/toast";

const ChangePasswordO = () => {
  const [form, setForm] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    label: "",
    color: "",
    requirements: {
      length: false,
      uppercase: false,
      lowercase: false,
      number: false,
      special: false,
    },
  });
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
    setErrors({ ...errors, [name]: "" });

    if (name === "newPassword") {
      validatePasswordStrength(value);
    }
  };

  const validatePasswordStrength = (password) => {
    const requirements = {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /[0-9]/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    };

    const metCount = Object.values(requirements).filter(Boolean).length;

    let score = 0;
    let label = "";
    let color = "";

    if (password.length === 0) {
      score = 0;
      label = "";
      color = "";
    } else if (metCount <= 2) {
      score = 1;
      label = "Weak";
      color = "text-red-500";
    } else if (metCount === 3) {
      score = 2;
      label = "Medium";
      color = "text-yellow-500";
    } else if (metCount >= 4) {
      score = 3;
      label = "Strong";
      color = "text-green-500";
    }

    setPasswordStrength({
      score,
      label,
      color,
      requirements,
    });
  };

  const handleSubmit = () => {
    const newErrors = {};

    // Check old password
    if (!form.oldPassword) {
      newErrors.oldPassword = "Old password is required";
      toastError("Old password is required");
      setErrors(newErrors);
      return;
    }

    // Check new password
    if (!form.newPassword) {
      newErrors.newPassword = "New password is required";
      toastError("New password is required");
      setErrors(newErrors);
      return;
    }

    if (form.newPassword.length < 8) {
      newErrors.newPassword = "Password must be at least 8 characters";
      toastError("Password must be at least 8 characters");
      setErrors(newErrors);
      return;
    }

    if (passwordStrength.score < 3) {
      newErrors.newPassword = "Please use a stronger password";
      toastError(
        "Please use a stronger password. Include uppercase, lowercase, numbers and special characters.",
      );
      setErrors(newErrors);
      return;
    }

    // Check confirm password
    if (!form.confirmPassword) {
      newErrors.confirmPassword = "Please confirm your password";
      toastError("Please confirm your password");
      setErrors(newErrors);
      return;
    }

    if (form.newPassword !== form.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
      toastError(
        "Passwords do not match. Please make sure both passwords are identical.",
      );
      setErrors(newErrors);
      return;
    }

    // All validations passed
    toastSuccess("Password updated successfully!");
    setForm({ oldPassword: "", newPassword: "", confirmPassword: "" });
    setPasswordStrength({
      score: 0,
      label: "",
      color: "",
      requirements: {
        length: false,
        uppercase: false,
        lowercase: false,
        number: false,
        special: false,
      },
    });
    setErrors({});
  };

  const getStrengthBarColor = () => {
    if (passwordStrength.score === 0) return "bg-gray-200";
    if (passwordStrength.score === 1) return "bg-red-500";
    if (passwordStrength.score === 2) return "bg-yellow-500";
    return "bg-green-500";
  };

  const getStrengthBarWidth = () => {
    if (passwordStrength.score === 0) return "0%";
    if (passwordStrength.score === 1) return "33%";
    if (passwordStrength.score === 2) return "66%";
    return "100%";
  };

  return (
    <div className="flex flex-col gap-6 max-w-2xl">
      {/* Header */}
      <Card className="bg-white border border-gray-200 px-4 sm:px-6 py-4 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
            <FontAwesomeIcon icon={faLock} className="text-blue-800 h-5 w-5" />
          </div>
          <div>
            <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">
              Change password
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">
              Update your account password to keep your account secure
            </p>
          </div>
        </div>
      </Card>

      {/* Password Form */}
      <Card className="bg-white border border-gray-200 shadow-sm p-6">
        <div className="space-y-6">
          {/* Old Password */}
          <div className="space-y-1.5">
            <Label className="text-sm font-medium text-gray-700 flex items-center gap-2">
              <FontAwesomeIcon
                icon={faKey}
                className="text-blue-600 h-3.5 w-3.5"
              />
              Old password
            </Label>
            <div className="relative">
              <Input
                type={showOldPassword ? "text" : "password"}
                name="oldPassword"
                value={form.oldPassword}
                onChange={handleChange}
                placeholder="Enter your current password"
                className={`pr-10 ${errors.oldPassword ? "border-red-500" : "border-gray-300"}`}
              />
              <button
                type="button"
                onClick={() => setShowOldPassword(!showOldPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <FontAwesomeIcon
                  icon={showOldPassword ? faEyeSlash : faEye}
                  className="h-4 w-4"
                />
              </button>
            </div>
            {errors.oldPassword && (
              <p className="text-red-500 text-xs flex items-center gap-1">
                <FontAwesomeIcon
                  icon={faExclamationCircle}
                  className="h-3 w-3"
                />
                {errors.oldPassword}
              </p>
            )}
          </div>

          {/* New Password */}
          <div className="space-y-1.5">
            <Label className="text-sm font-medium text-gray-700 flex items-center gap-2">
              <FontAwesomeIcon
                icon={faLock}
                className="text-blue-600 h-3.5 w-3.5"
              />
              New password
            </Label>
            <div className="relative">
              <Input
                type={showNewPassword ? "text" : "password"}
                name="newPassword"
                value={form.newPassword}
                onChange={handleChange}
                placeholder="Enter new password (min 8 characters)"
                className={`pr-10 ${errors.newPassword ? "border-red-500" : "border-gray-300"}`}
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <FontAwesomeIcon
                  icon={showNewPassword ? faEyeSlash : faEye}
                  className="h-4 w-4"
                />
              </button>
            </div>
            {errors.newPassword && (
              <p className="text-red-500 text-xs flex items-center gap-1">
                <FontAwesomeIcon
                  icon={faExclamationCircle}
                  className="h-3 w-3"
                />
                {errors.newPassword}
              </p>
            )}

            {/* Password Strength Indicator */}
            {form.newPassword.length > 0 && (
              <div className="mt-3 p-4 bg-gray-50 rounded-lg border border-gray-200 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 flex-1">
                    <div className="flex-1 bg-gray-200 rounded-full h-2.5 max-w-xs">
                      <div
                        className={`h-2.5 rounded-full transition-all duration-300 ${getStrengthBarColor()}`}
                        style={{ width: getStrengthBarWidth() }}
                      />
                    </div>
                    <span
                      className={`text-xs font-medium ${passwordStrength.color} min-w-[50px]`}
                    >
                      {passwordStrength.label}
                    </span>
                  </div>
                  <FontAwesomeIcon
                    icon={faShield}
                    className={`h-4 w-4 ${
                      passwordStrength.label === "Strong"
                        ? "text-green-500"
                        : passwordStrength.label === "Medium"
                          ? "text-yellow-500"
                          : passwordStrength.label === "Weak"
                            ? "text-red-500"
                            : "text-gray-300"
                    }`}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 pt-2 border-t border-gray-200">
                  <div
                    className={`flex items-center gap-1.5 text-xs ${passwordStrength.requirements.length ? "text-green-600" : "text-gray-400"}`}
                  >
                    {passwordStrength.requirements.length ? (
                      <FontAwesomeIcon
                        icon={faCheckCircle}
                        className="h-3 w-3 text-green-500"
                      />
                    ) : (
                      <FontAwesomeIcon
                        icon={faExclamationCircle}
                        className="h-3 w-3 text-gray-300"
                      />
                    )}
                    <span>At least 8 characters</span>
                  </div>
                  <div
                    className={`flex items-center gap-1.5 text-xs ${passwordStrength.requirements.uppercase ? "text-green-600" : "text-gray-400"}`}
                  >
                    {passwordStrength.requirements.uppercase ? (
                      <FontAwesomeIcon
                        icon={faCheckCircle}
                        className="h-3 w-3 text-green-500"
                      />
                    ) : (
                      <FontAwesomeIcon
                        icon={faExclamationCircle}
                        className="h-3 w-3 text-gray-300"
                      />
                    )}
                    <span>Uppercase letter (A-Z)</span>
                  </div>
                  <div
                    className={`flex items-center gap-1.5 text-xs ${passwordStrength.requirements.lowercase ? "text-green-600" : "text-gray-400"}`}
                  >
                    {passwordStrength.requirements.lowercase ? (
                      <FontAwesomeIcon
                        icon={faCheckCircle}
                        className="h-3 w-3 text-green-500"
                      />
                    ) : (
                      <FontAwesomeIcon
                        icon={faExclamationCircle}
                        className="h-3 w-3 text-gray-300"
                      />
                    )}
                    <span>Lowercase letter (a-z)</span>
                  </div>
                  <div
                    className={`flex items-center gap-1.5 text-xs ${passwordStrength.requirements.number ? "text-green-600" : "text-gray-400"}`}
                  >
                    {passwordStrength.requirements.number ? (
                      <FontAwesomeIcon
                        icon={faCheckCircle}
                        className="h-3 w-3 text-green-500"
                      />
                    ) : (
                      <FontAwesomeIcon
                        icon={faExclamationCircle}
                        className="h-3 w-3 text-gray-300"
                      />
                    )}
                    <span>Number (0-9)</span>
                  </div>
                  <div
                    className={`flex items-center gap-1.5 text-xs col-span-1 sm:col-span-2 ${passwordStrength.requirements.special ? "text-green-600" : "text-gray-400"}`}
                  >
                    {passwordStrength.requirements.special ? (
                      <FontAwesomeIcon
                        icon={faCheckCircle}
                        className="h-3 w-3 text-green-500"
                      />
                    ) : (
                      <FontAwesomeIcon
                        icon={faExclamationCircle}
                        className="h-3 w-3 text-gray-300"
                      />
                    )}
                    <span>Special character (!@#$%^&*(),.?":{}|&lt;&gt;)</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Confirm Password */}
          <div className="space-y-1.5">
            <Label className="text-sm font-medium text-gray-700 flex items-center gap-2">
              <FontAwesomeIcon
                icon={faCheckCircle}
                className="text-blue-600 h-3.5 w-3.5"
              />
              Confirm new password
            </Label>
            <div className="relative">
              <Input
                type={showConfirmPassword ? "text" : "password"}
                name="confirmPassword"
                value={form.confirmPassword}
                onChange={handleChange}
                placeholder="Confirm your new password"
                className={`pr-10 ${errors.confirmPassword ? "border-red-500" : "border-gray-300"}`}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <FontAwesomeIcon
                  icon={showConfirmPassword ? faEyeSlash : faEye}
                  className="h-4 w-4"
                />
              </button>
            </div>
            {errors.confirmPassword && (
              <p className="text-red-500 text-xs flex items-center gap-1">
                <FontAwesomeIcon
                  icon={faExclamationCircle}
                  className="h-3 w-3"
                />
                {errors.confirmPassword}
              </p>
            )}

            {/* Password Match Indicator */}
            {form.confirmPassword.length > 0 && form.newPassword.length > 0 && (
              <div
                className={`flex items-center gap-2 p-2.5 rounded-lg ${
                  form.newPassword === form.confirmPassword
                    ? "bg-green-50 border border-green-200"
                    : "bg-red-50 border border-red-200"
                }`}
              >
                {form.newPassword === form.confirmPassword ? (
                  <>
                    <FontAwesomeIcon
                      icon={faCheckCircle}
                      className="text-green-500 h-4 w-4"
                    />
                    <span className="text-green-700 text-xs font-medium">
                      Passwords match
                    </span>
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon
                      icon={faExclamationCircle}
                      className="text-red-500 h-4 w-4"
                    />
                    <span className="text-red-700 text-xs font-medium">
                      Passwords do not match
                    </span>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Submit Button */}
          <div className="flex justify-end pt-2 border-t border-gray-200 mt-2">
            <Button
              onClick={handleSubmit}
              className="gap-2 bg-blue-800 hover:bg-blue-900 text-white px-8 py-2.5"
            >
              <FontAwesomeIcon icon={faSave} className="h-4 w-4" />
              Change password
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default ChangePasswordO;
